/**
 * Jain Lexicon auth API. Base URL: VITE_API_BASE_URL (e.g. …/api), paths prefixed with /jain_lexicon/auth/.
 */

import {
  getStoredPortalRbac,
  getStoredStaffRbac,
  parseRbacFromAuthPayload,
  type JainLexiconRbacState,
} from "./jainLexiconRbac";
import { formatApiErrorBody } from "./formatApiError";

/** @deprecated Migrate to JL_PORTAL_* — read only for one-time migration. */
export const JL_ACCESS_TOKEN_KEY = "jl_access_token";
/** @deprecated Migrate to JL_PORTAL_* — read only for one-time migration. */
export const JL_REFRESH_TOKEN_KEY = "jl_refresh_token";

/** Contributor portal JWT keys (user site). */
export const JL_PORTAL_ACCESS_TOKEN_KEY = "jl_portal_access_token";
export const JL_PORTAL_REFRESH_TOKEN_KEY = "jl_portal_refresh_token";

/** Staff (admin/scholar) JWT keys — isolated from portal. */
export const JL_STAFF_ACCESS_TOKEN_KEY = "jl_staff_access_token";
export const JL_STAFF_REFRESH_TOKEN_KEY = "jl_staff_refresh_token";

export const JL_SIGNUP_ROLE_KEY = "jl_signup_chosen_role";

/** Role chosen during registration; used until logout for admin/scholar dashboard access. */
export type SignupChosenRole = "admin" | "contributor" | "scholar";

/** Maps to backend `account_type` / group assignment. */
export type AccountType = "admin" | "scholar" | "contributor";

export function getStoredSignupRole(): SignupChosenRole | null {
  if (typeof localStorage === "undefined") {
    return null;
  }
  const raw = localStorage.getItem(JL_SIGNUP_ROLE_KEY);
  if (raw === "admin" || raw === "contributor" || raw === "scholar") {
    return raw;
  }
  return null;
}

export function setStoredSignupRole(role: SignupChosenRole | null): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  if (role) {
    localStorage.setItem(JL_SIGNUP_ROLE_KEY, role);
  } else {
    localStorage.removeItem(JL_SIGNUP_ROLE_KEY);
  }
}

const AUTH_PREFIX = "/jain_lexicon/auth";

export const AUTH_PATHS = {
  login: `${AUTH_PREFIX}/login/`,
  signupSendOtp: `${AUTH_PREFIX}/signup/send-otp/`,
  signupVerifyOtp: `${AUTH_PREFIX}/signup/verify-otp/`,
  signupResendOtp: `${AUTH_PREFIX}/signup/resend-otp/`,
  signupComplete: `${AUTH_PREFIX}/signup/complete/`,
  tokenRefresh: `${AUTH_PREFIX}/token/refresh/`,
  logout: `${AUTH_PREFIX}/logout/`,
  passwordForgot: `${AUTH_PREFIX}/password/forgot/`,
  passwordReset: `${AUTH_PREFIX}/password/reset/`,
  passwordChange: `${AUTH_PREFIX}/password/change/`,
} as const;

export type StaffRole = "admin" | "scholar";

/**
 * POST `…/login/` body field — required by the server; maps UI to Django group checks:
 * `"admin"` → `jain_lexicon_admin`, `"scholar"` → `jain_lexicon_scholar`, `"portal"` → `jain_lexicon_contributer`.
 * Wrong portal → HTTP 403, `{ "detail": "This account is not allowed to access this portal." }`, no tokens.
 * Client `role` on the login body is ignored for authorization.
 */
export type AuthLoginContext = "admin" | "scholar" | "portal";

export const AUTH_LOGIN_CONTEXT: Record<AuthLoginContext, AuthLoginContext> = {
  admin: "admin",
  scholar: "scholar",
  portal: "portal",
};

function portalEnv(key: string, fallback: string): string {
  const raw =
    typeof import.meta !== "undefined"
      ? (import.meta.env as Record<string, string | undefined>)[key]
      : undefined;
  const s = (raw ?? "").trim();
  const use = s.length ? s : fallback;
  return use.startsWith("/") ? use : `/${use}`;
}

/** Portal/password endpoints (`/auth/...`). Override with `VITE_PORTAL_*` env vars. */
export function getPortalPaths() {
  return {
    login: portalEnv("VITE_PORTAL_LOGIN_PATH", "/auth/login/"),
    tokenRefresh: portalEnv(
      "VITE_PORTAL_TOKEN_REFRESH_PATH",
      "/auth/token/refresh/"
    ),
    logout: portalEnv("VITE_PORTAL_LOGOUT_PATH", "/auth/logout/"),
    /** Same Jain Lexicon tenant routes as `AUTH_PATHS` (`/jain_lexicon/auth/...`). */
    passwordForgot: portalEnv(
      "VITE_PORTAL_PASSWORD_FORGOT_PATH",
      AUTH_PATHS.passwordForgot
    ),
    passwordReset: portalEnv(
      "VITE_PORTAL_PASSWORD_RESET_PATH",
      AUTH_PATHS.passwordReset
    ),
    passwordChange: portalEnv(
      "VITE_PORTAL_PASSWORD_CHANGE_PATH",
      AUTH_PATHS.passwordChange
    ),
  };
}

export function validateSignupPassword(password: string): string | null {
  const minLen = 8;
  if (password.length < minLen) {
    return `Password must be at least ${minLen} characters.`;
  }
  if (/^\d+$/.test(password)) {
    return "Password cannot consist of digits only.";
  }
  return null;
}

function apiBase(): string {
  const raw =
    typeof import.meta !== "undefined"
      ? (import.meta.env.VITE_API_BASE_URL as string | undefined)
      : undefined;
  return (raw ?? "").replace(/\/$/, "");
}

function tenantHeaders(): Record<string, string> {
  const id =
    typeof import.meta !== "undefined"
      ? (import.meta.env.VITE_X_TENANTID as string | undefined)
      : undefined;
  const trimmed = (id ?? "").replace(/^\uFEFF/, "").trim();
  if (!trimmed) {
    return {};
  }
  /*
   * Single header only — duplicate keys that differ only by case merge in fetch
   * into one comma-separated value and break validators ("Invalid tenant_id format").
   */
  return { "X-Tenant-Id": trimmed };
}

/** Optional `tenant_id` for signup OTP / complete JSON bodies when tenant-scoped. */
function signupTenantBody(): Record<string, unknown> {
  const id =
    typeof import.meta !== "undefined"
      ? (import.meta.env.VITE_X_TENANTID as string | undefined)
      : undefined;
  const trimmed = (id ?? "").replace(/^\uFEFF/, "").trim();
  if (!trimmed) {
    return {};
  }
  return { tenant_id: trimmed };
}

function collectResponseObjects(
  raw: Record<string, unknown> | null
): Record<string, unknown>[] {
  if (!raw) {
    return [];
  }
  const blobs: Record<string, unknown>[] = [raw];

  const data = raw.data;
  if (data !== null && typeof data === "object" && !Array.isArray(data)) {
    blobs.push(data as Record<string, unknown>);
  }

  const result = raw.result;
  if (result !== null && typeof result === "object" && !Array.isArray(result)) {
    blobs.push(result as Record<string, unknown>);
  }

  return blobs;
}

function pickString(
  record: Record<string, unknown> | null,
  keys: string[]
): string | undefined {
  const blobs = collectResponseObjects(record);
  for (const u of blobs) {
    for (const k of keys) {
      const v = u[k];
      if (typeof v === "string" && v.length > 0) {
        return v;
      }
    }
  }
  return undefined;
}

function jwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2) {
      return null;
    }
    let base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = base64.length % 4;
    if (pad) {
      base64 += "=".repeat(4 - pad);
    }
    const json = atob(base64);
    const parsed = JSON.parse(json);
    return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/** Shown when the tenant marks the account inactive (`is_active` false or JWT equivalent). */
export const INACTIVE_ACCOUNT_MESSAGE =
  "This account has been deactivated. Contact an administrator if you need access.";

function boolIndicatesInactiveFlag(v: unknown): boolean {
  return (
    v === false ||
    v === 0 ||
    v === "false" ||
    v === "0" ||
    (typeof v === "string" && v.trim().toLowerCase() === "inactive")
  );
}

function recordIndicatesInactiveUser(r: Record<string, unknown>): boolean {
  for (const key of ["is_active", "active", "is_enabled"] as const) {
    if (Object.prototype.hasOwnProperty.call(r, key)) {
      if (boolIndicatesInactiveFlag(r[key])) {
        return true;
      }
    }
  }
  return false;
}

/** True when JWT payload explicitly marks the user inactive (claim shapes vary by backend). */
export function accessTokenIndicatesInactiveAccount(token: string): boolean {
  const payload = jwtPayload(token);
  if (!payload) {
    return false;
  }
  if (recordIndicatesInactiveUser(payload)) {
    return true;
  }
  const nestedUser = payload.user;
  if (
    nestedUser &&
    typeof nestedUser === "object" &&
    !Array.isArray(nestedUser)
  ) {
    return recordIndicatesInactiveUser(nestedUser as Record<string, unknown>);
  }
  return false;
}

function authLoginPayloadIndicatesInactiveUser(
  data: Record<string, unknown> | null,
  raw: unknown,
  accessToken: string | null
): boolean {
  if (accessToken && accessTokenIndicatesInactiveAccount(accessToken)) {
    return true;
  }
  const roots: unknown[] = [data, raw];
  if (data?.data) {
    roots.push(data.data);
  }
  for (const root of roots) {
    if (!root || typeof root !== "object" || Array.isArray(root)) {
      continue;
    }
    const o = root as Record<string, unknown>;
    const nestedUser =
      o.user ??
      o.profile ??
      (o.data !== undefined &&
      typeof o.data === "object" &&
      o.data !== null &&
      !Array.isArray(o.data)
        ? (o.data as Record<string, unknown>).user
        : undefined);
    if (recordIndicatesInactiveUser(o)) {
      return true;
    }
    if (
      nestedUser &&
      typeof nestedUser === "object" &&
      !Array.isArray(nestedUser) &&
      recordIndicatesInactiveUser(nestedUser as Record<string, unknown>)
    ) {
      return true;
    }
  }
  return false;
}

export function decodeUserFromToken(token: string): { id: string; email: string | null } | null {
  const payload = jwtPayload(token);
  if (!payload) {
    return null;
  }

  const id =
    (typeof payload.user_id === "string" && payload.user_id) ||
    (typeof payload.sub === "string" && payload.sub) ||
    (typeof payload.uid === "string" && payload.uid) ||
    null;

  const email =
    (typeof payload.email === "string" && payload.email) ||
    (typeof payload.user_email === "string" && payload.user_email) ||
    null;

  if (!id) {
    return null;
  }
  return { id, email };
}

/** Staff dashboard role from JWT (admin or scholar). */
export function decodeStaffRoleFromToken(token: string): StaffRole | null {
  const payload = jwtPayload(token);
  if (!payload) {
    return null;
  }
  const lc = (v: unknown) =>
    typeof v === "string" ? v.toLowerCase().trim() : "";

  const slug = (raw: string) => {
    const t = lc(raw);
    if (t.startsWith("role_")) {
      return t.slice(5);
    }
    if (t.startsWith("role")) {
      return t.slice(4);
    }
    return t;
  };

  const fromSlug = (s: string): StaffRole | null => {
    const t = slug(s);
    if (
      t === "tenant_admin" ||
      t === "admin" ||
      t === "superuser" ||
      t === "super_admin" ||
      t === "superadmin"
    ) {
      return "admin";
    }
    if (
      t === "tenant_scholar" ||
      t === "scholar" ||
      t === "staff" ||
      t === "editor"
    ) {
      return "scholar";
    }
    return null;
  };

  const roleStr =
    lc(payload.role) ||
    lc(payload.user_role) ||
    lc(payload.signup_role) ||
    lc(payload.portal_role);

  const fromRoleString = fromSlug(roleStr);
  if (fromRoleString) {
    return fromRoleString;
  }

  const nestedUser = payload.user;
  if (nestedUser && typeof nestedUser === "object" && !Array.isArray(nestedUser)) {
    const u = nestedUser as Record<string, unknown>;
    const nested =
      fromSlug(lc(u.role)) ||
      fromSlug(lc(u.user_role)) ||
      fromSlug(lc(u.signup_role));
    if (nested) {
      return nested;
    }
  }

  const scanStringArray = (arr: unknown): StaffRole | null => {
    if (!Array.isArray(arr)) {
      return null;
    }
    for (const x of arr) {
      if (typeof x === "string") {
        const m = fromSlug(x);
        if (m) {
          return m;
        }
      }
    }
    return null;
  };

  const fromGroups = scanStringArray(payload.groups);
  if (fromGroups) {
    return fromGroups;
  }

  const lexiconStaffFromGroupName = (name: string): StaffRole | null => {
    const n = lc(name).replace(/\s+/g, "");
    if (n.includes("jain_lexicon_admin") || n.endsWith("_admin")) {
      return "admin";
    }
    if (n.includes("scholar")) {
      return "scholar";
    }
    return null;
  };

  const fromGroupObjects = (arr: unknown): StaffRole | null => {
    if (!Array.isArray(arr)) {
      return null;
    }
    let scholarHit = false;
    for (const item of arr) {
      let name = "";
      if (typeof item === "string") {
        name = item;
      } else if (item && typeof item === "object" && !Array.isArray(item)) {
        const nm = (item as Record<string, unknown>).name;
        if (typeof nm === "string") {
          name = nm;
        }
      }
      const m = fromSlug(name) ?? lexiconStaffFromGroupName(name);
      if (m === "admin") {
        return "admin";
      }
      if (m === "scholar") {
        scholarHit = true;
      }
    }
    return scholarHit ? "scholar" : null;
  };

  const fromPayloadGroups = fromGroupObjects(payload.groups);
  if (fromPayloadGroups) {
    return fromPayloadGroups;
  }

  if (nestedUser && typeof nestedUser === "object" && !Array.isArray(nestedUser)) {
    const nestedGroups = fromGroupObjects(
      (nestedUser as Record<string, unknown>).groups
    );
    if (nestedGroups) {
      return nestedGroups;
    }
  }

  const fromRoles = scanStringArray(payload.roles);
  if (fromRoles) {
    return fromRoles;
  }

  const authorities = payload.authorities;
  if (Array.isArray(authorities)) {
    for (const a of authorities) {
      let s = "";
      if (typeof a === "string") {
        s = a;
      } else if (a && typeof a === "object" && "authority" in a) {
        const auth = (a as { authority?: unknown }).authority;
        if (typeof auth === "string") {
          s = auth;
        }
      }
      const m = fromSlug(s);
      if (m) {
        return m;
      }
    }
  }

  const realm = payload.realm_access;
  if (realm && typeof realm === "object" && realm !== null) {
    const fromRealm = scanStringArray(
      (realm as { roles?: unknown }).roles
    );
    if (fromRealm) {
      return fromRealm;
    }
  }

  if (payload.is_superuser === true) {
    return "admin";
  }
  if (payload.is_staff === true) {
    return "admin";
  }

  /* Staff login issued a valid user JWT but no recognizable role claim yet (RBAC not on token). Allow dashboard access as scholar until the backend adds claims. */
  if (decodeUserFromToken(token)) {
    return "scholar";
  }

  return null;
}

function migrateLegacyTokensToPortal(): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  const hasPortal = !!localStorage.getItem(JL_PORTAL_ACCESS_TOKEN_KEY);
  const legacyAccess = localStorage.getItem(JL_ACCESS_TOKEN_KEY);
  if (!hasPortal && legacyAccess) {
    localStorage.setItem(JL_PORTAL_ACCESS_TOKEN_KEY, legacyAccess);
    const lr = localStorage.getItem(JL_REFRESH_TOKEN_KEY);
    if (lr) {
      localStorage.setItem(JL_PORTAL_REFRESH_TOKEN_KEY, lr);
    }
    localStorage.removeItem(JL_ACCESS_TOKEN_KEY);
    localStorage.removeItem(JL_REFRESH_TOKEN_KEY);
  }
}

function normalizeErrorMessage(data: unknown): string | null {
  if (data === null || data === undefined) {
    return null;
  }
  if (typeof data === "string") {
    const trimmed = data.trim();
    if (!trimmed) {
      return null;
    }
    const looksJson =
      (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
      (trimmed.startsWith("[") && trimmed.endsWith("]"));
    if (looksJson) {
      try {
        return (
          formatApiErrorBody(JSON.parse(trimmed) as unknown) ?? trimmed
        );
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }

  const formatted = formatApiErrorBody(data);
  if (formatted) {
    return formatted;
  }

  if (typeof data !== "object") {
    return null;
  }

  const o = data as Record<string, unknown>;
  const d = o.detail;
  if (Array.isArray(d)) {
    const parts = d
      .map(item => {
        if (typeof item === "string") {
          return item.trim();
        }
        if (item && typeof item === "object" && !Array.isArray(item)) {
          const nested = formatApiErrorBody(item);
          if (nested) {
            return nested;
          }
          const msg = (item as { msg?: unknown }).msg;
          if (typeof msg === "string" && msg.trim()) {
            return msg.trim();
          }
        }
        return "";
      })
      .filter(Boolean);
    if (parts.length) {
      return parts.join("; ");
    }
  }

  return null;
}

function extractAccessToken(data: Record<string, unknown> | null): string | null {
  const blobs = collectResponseObjects(data);
  for (const u of blobs) {
    const candidates = [
      u.access,
      u.access_token,
      u.token,
      (u.data as Record<string, unknown> | undefined)?.access,
      (u.data as Record<string, unknown> | undefined)?.access_token,
    ];
    for (const c of candidates) {
      if (typeof c === "string" && c.length > 0) {
        return c;
      }
    }
  }
  return null;
}

function extractRefreshToken(data: Record<string, unknown> | null): string | null {
  const blobs = collectResponseObjects(data);
  for (const u of blobs) {
    const candidates = [
      u.refresh,
      u.refresh_token,
      (u.data as Record<string, unknown> | undefined)?.refresh,
    ];
    for (const c of candidates) {
      if (typeof c === "string" && c.length > 0) {
        return c;
      }
    }
  }
  return null;
}

/** Contributor portal access token (same as legacy getter for compatibility). */
export function getContributorAccessToken(): string | null {
  if (typeof localStorage === "undefined") {
    return null;
  }
  migrateLegacyTokensToPortal();
  return localStorage.getItem(JL_PORTAL_ACCESS_TOKEN_KEY);
}

export function getContributorRefreshToken(): string | null {
  if (typeof localStorage === "undefined") {
    return null;
  }
  migrateLegacyTokensToPortal();
  return localStorage.getItem(JL_PORTAL_REFRESH_TOKEN_KEY);
}

export function setContributorTokens(
  access: string | null,
  refresh: string | null
): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  if (access) {
    localStorage.setItem(JL_PORTAL_ACCESS_TOKEN_KEY, access);
  } else {
    localStorage.removeItem(JL_PORTAL_ACCESS_TOKEN_KEY);
  }
  if (refresh) {
    localStorage.setItem(JL_PORTAL_REFRESH_TOKEN_KEY, refresh);
  } else {
    localStorage.removeItem(JL_PORTAL_REFRESH_TOKEN_KEY);
  }
  localStorage.removeItem(JL_ACCESS_TOKEN_KEY);
  localStorage.removeItem(JL_REFRESH_TOKEN_KEY);
}

/** Staff (admin/scholar) access token — never mixed with portal. */
export function getStaffAccessToken(): string | null {
  if (typeof localStorage === "undefined") {
    return null;
  }
  return localStorage.getItem(JL_STAFF_ACCESS_TOKEN_KEY);
}

export function getStaffRefreshToken(): string | null {
  if (typeof localStorage === "undefined") {
    return null;
  }
  return localStorage.getItem(JL_STAFF_REFRESH_TOKEN_KEY);
}

export function setStaffTokens(access: string | null, refresh: string | null): void {
  if (typeof localStorage === "undefined") {
    return;
  }
  if (access) {
    localStorage.setItem(JL_STAFF_ACCESS_TOKEN_KEY, access);
  } else {
    localStorage.removeItem(JL_STAFF_ACCESS_TOKEN_KEY);
  }
  if (refresh) {
    localStorage.setItem(JL_STAFF_REFRESH_TOKEN_KEY, refresh);
  } else {
    localStorage.removeItem(JL_STAFF_REFRESH_TOKEN_KEY);
  }
}

/** @deprecated Prefer getContributorAccessToken(). */
export function getStoredAccessToken(): string | null {
  return getContributorAccessToken();
}

/** @deprecated Prefer getContributorRefreshToken(). */
export function getStoredRefreshToken(): string | null {
  return getContributorRefreshToken();
}

/** @deprecated Prefer setContributorTokens(). */
export function setStoredAccessToken(token: string | null): void {
  const r = getContributorRefreshToken();
  setContributorTokens(token, r);
}

/** @deprecated Prefer setContributorTokens(). */
export function setStoredRefreshToken(token: string | null): void {
  const a = getContributorAccessToken();
  setContributorTokens(a, token);
}
async function readJsonSafe(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) {
    return null;
  }
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return { message: text };
  }
}

async function authPost(
  path: string,
  body: Record<string, unknown>,
  options?: { bearer?: string | null }
): Promise<{ ok: boolean; status: number; data: Record<string, unknown> | null; raw: unknown }> {
  const base = apiBase();
  if (!base) {
    return {
      ok: false,
      status: 0,
      data: null,
      raw: null,
    };
  }
  const headers: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "application/json",
    ...tenantHeaders(),
  };
  if (options?.bearer) {
    headers.Authorization = `Bearer ${options.bearer}`;
  }

  const res = await fetch(`${base}${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  const raw = await readJsonSafe(res);
  const data =
    raw !== null &&
    typeof raw === "object" &&
    !Array.isArray(raw) &&
    !(raw instanceof Error)
      ? (raw as Record<string, unknown>)
      : null;
  return { ok: res.ok, status: res.status, data, raw };
}

async function authJson(
  method: "GET" | "PUT",
  path: string,
  options?: { bearer?: string | null; body?: Record<string, unknown> | null }
): Promise<{ ok: boolean; status: number; data: Record<string, unknown> | null; raw: unknown }> {
  const base = apiBase();
  if (!base) {
    return {
      ok: false,
      status: 0,
      data: null,
      raw: null,
    };
  }
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...tenantHeaders(),
  };
  if (options?.bearer) {
    headers.Authorization = `Bearer ${options.bearer}`;
  }
  let reqBody: string | undefined;
  if (
    options?.body !== undefined &&
    options?.body !== null &&
    Object.keys(options.body).length >= 0
  ) {
    headers["Content-Type"] = "application/json";
    reqBody = JSON.stringify(options.body);
  }

  const res = await fetch(`${base}${path}`, {
    method,
    headers,
    ...(reqBody !== undefined ? { body: reqBody } : {}),
  });
  const raw = await readJsonSafe(res);
  const data =
    raw !== null &&
    typeof raw === "object" &&
    !Array.isArray(raw) &&
    !(raw instanceof Error)
      ? (raw as Record<string, unknown>)
      : null;
  return { ok: res.ok, status: res.status, data, raw };
}

/**
 * Restore portal RBAC from localStorage. Values are written on login/signup when the
 * API returns `user` with group/permissions (see `parseRbacFromAuthPayload`).
 *
 * There is **no** `/auth/me/` (or `/jain_lexicon/auth/me/`) on this stack — do not add
 * a network call here.
 */
export async function syncPortalRbacFromServer(): Promise<{
  rbac: JainLexiconRbacState | null;
  unauthorized: boolean;
}> {
  return { rbac: getStoredPortalRbac(), unauthorized: false };
}

/**
 * Same as `syncPortalRbacFromServer` for staff: reads RBAC persisted from the last
 * staff login response (no remote profile endpoint).
 */
export async function syncStaffRbacFromServer(): Promise<{
  rbac: JainLexiconRbacState | null;
  unauthorized: boolean;
}> {
  return { rbac: getStoredStaffRbac(), unauthorized: false };
}

/** User profile (`GET`/`PUT` …/auth/profile/{user_id}/`). */
export type JainLexiconProfile = {
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
};

export function authProfilePath(userId: string): string {
  return `${AUTH_PREFIX}/profile/${encodeURIComponent(userId.trim())}/`;
}

function pickProfileField(o: Record<string, unknown>, keys: string[]): string | null {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === "string" && v.trim().length > 0) {
      return v.trim();
    }
  }
  return null;
}

function profileFromRecords(records: Record<string, unknown>[]): JainLexiconProfile | null {
  for (const o of records) {
    const email = pickProfileField(o, ["email"]);
    const first = pickProfileField(o, ["first_name", "firstName"]);
    const last = pickProfileField(o, ["last_name", "lastName"]);
    let full = pickProfileField(o, ["full_name", "fullName", "name"]);
    if (!full && (first || last)) {
      full = `${first ?? ""} ${last ?? ""}`.trim() || null;
    }
    if (email ?? first ?? last ?? full) {
      return { email, first_name: first, last_name: last, full_name: full };
    }
  }
  return null;
}

function normalizeProfilePayload(
  blob: Record<string, unknown> | null,
  raw: unknown
): JainLexiconProfile | null {
  const candidates: Record<string, unknown>[] = [];
  if (blob) {
    candidates.push(blob);
    const d = blob.data;
    if (d && typeof d === "object" && !Array.isArray(d)) {
      candidates.push(d as Record<string, unknown>);
    }
    const r = blob.result;
    if (r && typeof r === "object" && !Array.isArray(r)) {
      candidates.push(r as Record<string, unknown>);
    }
  }
  if (
    raw &&
    typeof raw === "object" &&
    !Array.isArray(raw) &&
    raw !== blob
  ) {
    candidates.push(raw as Record<string, unknown>);
  }
  return profileFromRecords(candidates);
}

/** Load profile for portal or staff JWT. */
export async function fetchProfileRemote(
  userId: string,
  scope: "portal" | "staff"
): Promise<{ profile: JainLexiconProfile | null; error: Error | null }> {
  const base = apiBase();
  const uid = userId.trim();
  if (!base || !uid) {
    return {
      profile: null,
      error: new Error(!base ? "VITE_API_BASE_URL is not set." : "Missing user id."),
    };
  }
  const bearer =
    scope === "portal" ? getContributorAccessToken() : getStaffAccessToken();
  if (!bearer) {
    return { profile: null, error: new Error("Not signed in.") };
  }

  let { ok, status, data, raw } = await authJson(
    "GET",
    authProfilePath(uid),
    { bearer }
  );

  /** Some stacks only expose PATCH/PUT — try PATCH if GET unsupported. */
  if (!ok && (status === 405 || status === 404)) {
    const fallback = await authJson(
      "GET",
      `${AUTH_PREFIX}/profile/${encodeURIComponent(uid)}`,
      { bearer }
    );
    ok = fallback.ok;
    status = fallback.status;
    data = fallback.data;
    raw = fallback.raw;
  }

  if (!ok) {
    return {
      profile: null,
      error: toError("Could not load profile.", data, raw, status),
    };
  }

  const profile = normalizeProfilePayload(data, raw);
  return {
    profile: profile ?? null,
    error: null,
  };
}

export type ProfileWritePayload = {
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
};

/** Create `full_name` from parts when omitted. */
export function buildFullName(first: string, last: string): string {
  return `${first.trim()} ${last.trim()}`.trim();
}

/** PUT profile (portal contributor or staff token). */
export async function updateProfileRemote(
  userId: string,
  payload: ProfileWritePayload,
  scope: "portal" | "staff"
): Promise<{ error: Error | null }> {
  const base = apiBase();
  const uid = userId.trim();
  if (!base || !uid) {
    return {
      error: new Error(!base ? "VITE_API_BASE_URL is not set." : "Missing user id."),
    };
  }
  const bearer =
    scope === "portal" ? getContributorAccessToken() : getStaffAccessToken();
  if (!bearer) {
    return { error: new Error("Not signed in.") };
  }

  const body: Record<string, unknown> = {
    email: payload.email.trim(),
    first_name: payload.first_name.trim(),
    last_name: payload.last_name.trim(),
    full_name:
      payload.full_name.trim() ||
      buildFullName(payload.first_name, payload.last_name) ||
      payload.email.trim(),
  };

  const { ok, status, data, raw } = await authJson(
    "PUT",
    authProfilePath(uid),
    {
      bearer,
      body,
    }
  );

  if (ok) {
    return { error: null };
  }
  return {
    error: toError("Could not save profile.", data, raw, status),
  };
}

function toError(
  fallback: string,
  body: Record<string, unknown> | null,
  raw: unknown,
  status: number
): Error {
  const msg =
    normalizeErrorMessage(body ?? raw ?? null) ??
    (status === 404 ? `Not found (${status}).` : null);
  return new Error(msg || fallback || `Request failed (${status}).`);
}

export async function sendSignupOtpEmail(
  email: string,
  options?: {
    /** Panel role sent on complete; also sent early as `role` when set. */
    role?: AccountType;
    /** @deprecated Prefer `role` — sent as legacy `account_type` if backend still expects it */
    account_type?: AccountType;
  }
): Promise<{ codeReference: string | null; error: Error | null }> {
  if (!apiBase()) {
    return { codeReference: null, error: new Error("VITE_API_BASE_URL is not set.") };
  }
  if (!email.trim()) {
    return { codeReference: null, error: new Error("Email is required.") };
  }
  const lane = options?.role ?? options?.account_type;
  const body: Record<string, unknown> = {
    email: email.trim(),
    ...signupTenantBody(),
  };
  if (lane) {
    body.role = lane;
    body.account_type = lane;
  }
  const { ok, status, data, raw } = await authPost(AUTH_PATHS.signupSendOtp, body);
  if (!ok) {
    return { codeReference: null, error: toError("Could not send OTP.", data, raw, status) };
  }
  const ref =
    pickString(data, ["code_reference", "codeReference", "reference"]) ?? null;
  return { codeReference: ref, error: ref ? null : new Error("Missing code reference from server.") };
}

export async function verifySignupOtp(
  email: string,
  otp: string,
  codeReference: string
): Promise<{ verificationToken: string | null; error: Error | null }> {
  if (!apiBase()) {
    return { verificationToken: null, error: new Error("VITE_API_BASE_URL is not set.") };
  }
  const code = otp.trim().replace(/\s/g, "");
  if (!email.trim()) {
    return { verificationToken: null, error: new Error("Email is required.") };
  }
  if (!code) {
    return { verificationToken: null, error: new Error("Enter the OTP from your email.") };
  }
  if (!codeReference.trim()) {
    return { verificationToken: null, error: new Error("Missing OTP session. Request a new code.") };
  }

  const { ok, status, data, raw } = await authPost(AUTH_PATHS.signupVerifyOtp, {
    email: email.trim(),
    otp: code,
    code_reference: codeReference.trim(),
    ...signupTenantBody(),
  });
  if (!ok) {
    return { verificationToken: null, error: toError("OTP verification failed.", data, raw, status) };
  }
  const token =
    pickString(data, ["verification_token", "verificationToken", "token"]) ??
    null;
  return {
    verificationToken: token,
    error: token ? null : new Error("Missing verification token from server."),
  };
}

export async function resendSignupOtp(
  email: string,
  codeReference: string
): Promise<{ codeReference: string | null; error: Error | null }> {
  if (!apiBase()) {
    return { codeReference: null, error: new Error("VITE_API_BASE_URL is not set.") };
  }
  if (!email.trim() || !codeReference.trim()) {
    return { codeReference: null, error: new Error("Email and code reference are required.") };
  }
  const { ok, status, data, raw } = await authPost(AUTH_PATHS.signupResendOtp, {
    email: email.trim(),
    code_reference: codeReference.trim(),
    ...signupTenantBody(),
  });
  if (!ok) {
    return { codeReference: null, error: toError("Could not resend OTP.", data, raw, status) };
  }
  const next =
    pickString(data, ["code_reference", "codeReference", "reference"]) ?? codeReference;
  return { codeReference: next, error: null };
}

export type SignupCompletePayload = {
  email: string;
  password: string;
  password_confirm: string;
  verification_token: string;
  /**
   * Required for panel signup: canonical API field **`role`** —
   * `"admin"` | `"scholar"` | `"contributor"` per signup surface.
   */
  role: SignupChosenRole;
  /**
   * @deprecated Omit if backend only uses `role`. When set, echoed as legacy `account_type`.
   */
  account_type?: AccountType;
};

export type SignupCompleteResult = {
  error: Error | null;
  access?: string | null;
  refresh?: string | null;
  rbac?: JainLexiconRbacState | null;
};

export async function signupComplete(
  payload: SignupCompletePayload
): Promise<SignupCompleteResult> {
  if (!apiBase()) {
    return { error: new Error("VITE_API_BASE_URL is not set.") };
  }
  const accountType = payload.account_type ?? payload.role;
  const body: Record<string, unknown> = {
    email: payload.email.trim(),
    password: payload.password,
    password_confirm: payload.password_confirm,
    verification_token: payload.verification_token,
    /** Canonical field per backend integration guide */
    role: payload.role,
    /** Legacy duplicates — remove once API no longer expects them */
    signup_role: payload.role,
    account_type: accountType,
    ...signupTenantBody(),
  };

  const { ok, status, data, raw } = await authPost(AUTH_PATHS.signupComplete, body);
  if (ok) {
    const access = data ? extractAccessToken(data) : null;
    const refresh = data ? extractRefreshToken(data) : null;
    const rbac = data
      ? parseRbacFromAuthPayload(data, raw)
      : null;
    if (access && authLoginPayloadIndicatesInactiveUser(data, raw, access)) {
      return {
        error: new Error(INACTIVE_ACCOUNT_MESSAGE),
        access: null,
        refresh: null,
        rbac: null,
      };
    }
    return { error: null, access, refresh, rbac };
  }
  return { error: toError("Sign up failed.", data, raw, status) };
}

export async function loginPortalPassword(
  email: string,
  password: string
): Promise<{
  access: string | null;
  refresh: string | null;
  rbac: JainLexiconRbacState | null;
  error: Error | null;
}> {
  if (!apiBase()) {
    return {
      access: null,
      refresh: null,
      rbac: null,
      error: new Error("VITE_API_BASE_URL is not set."),
    };
  }
  const { ok, status, data, raw } = await authPost(getPortalPaths().login, {
    email: email.trim(),
    password,
    login_context: AUTH_LOGIN_CONTEXT.portal,
    ...signupTenantBody(),
  });
  const access = ok && data ? extractAccessToken(data) : null;
  const refresh = ok && data ? extractRefreshToken(data) : null;
  const rbac =
    ok && data ? parseRbacFromAuthPayload(data, raw) : null;
  if (ok && access) {
    if (authLoginPayloadIndicatesInactiveUser(data, raw, access)) {
      return {
        access: null,
        refresh: null,
        rbac: null,
        error: new Error(INACTIVE_ACCOUNT_MESSAGE),
      };
    }
    return { access, refresh, rbac, error: null };
  }
  const err = ok
    ? new Error(
        normalizeErrorMessage(data ?? raw) ?? "Login did not return an access token."
      )
    : toError("Login failed.", data, raw, status);
  return { access: null, refresh: null, rbac: null, error: err };
}

export async function loginStaffPassword(
  email: string,
  password: string,
  /** Admin vs Scholar tab — sent as `login_context` (`"admin"` | `"scholar"`); not the legacy combined staff value. */
  loginContext: StaffRole
): Promise<{
  access: string | null;
  refresh: string | null;
  staffRole: StaffRole | null;
  rbac: JainLexiconRbacState | null;
  error: Error | null;
}> {
  if (!apiBase()) {
    return {
      access: null,
      refresh: null,
      staffRole: null,
      rbac: null,
      error: new Error("VITE_API_BASE_URL is not set."),
    };
  }
  const { ok, status, data, raw } = await authPost(AUTH_PATHS.login, {
    email: email.trim(),
    password,
    login_context: AUTH_LOGIN_CONTEXT[loginContext],
    ...signupTenantBody(),
  });
  const access = ok && data ? extractAccessToken(data) : null;
  const refresh = ok && data ? extractRefreshToken(data) : null;
  const rbac =
    ok && data ? parseRbacFromAuthPayload(data, raw) : null;
  if (ok && access) {
    if (authLoginPayloadIndicatesInactiveUser(data, raw, access)) {
      return {
        access: null,
        refresh: null,
        staffRole: null,
        rbac: null,
        error: new Error(INACTIVE_ACCOUNT_MESSAGE),
      };
    }
    const staffRole = decodeStaffRoleFromToken(access) ?? loginContext;
    return { access, refresh, staffRole, rbac, error: null };
  }
  const err = ok
    ? new Error(
        normalizeErrorMessage(data ?? raw) ?? "Login did not return an access token."
      )
    : toError("Staff login failed.", data, raw, status);
  return { access: null, refresh: null, staffRole: null, rbac: null, error: err };
}

/** @deprecated Use loginStaffPassword (admin/scholar) or loginPortalPassword (contributor). */
export async function loginWithPassword(
  email: string,
  password: string,
  /** @deprecated Ambiguous — pass explicit staff tab; defaults to scholar. */
  staffLoginContext: StaffRole = "scholar"
): Promise<{
  access: string | null;
  refresh: string | null;
  error: Error | null;
}> {
  return loginStaffPassword(email, password, staffLoginContext).then(
    ({ access, refresh, error }) => ({
      access,
      refresh,
      error,
    })
  );
}

export async function refreshPortalAccessToken(refresh?: string | null): Promise<{
  access: string | null;
  refresh: string | null;
  error: Error | null;
}> {
  if (!apiBase()) {
    return { access: null, refresh: null, error: new Error("VITE_API_BASE_URL is not set.") };
  }
  const r = refresh ?? getContributorRefreshToken();
  if (!r) {
    return { access: null, refresh: null, error: new Error("No refresh token.") };
  }
  const { ok, status, data, raw } = await authPost(getPortalPaths().tokenRefresh, {
    refresh: r,
  });
  const access = ok && data ? extractAccessToken(data) : null;
  const nextRefresh = ok && data ? extractRefreshToken(data) ?? r : null;
  if (ok && access) {
    if (authLoginPayloadIndicatesInactiveUser(data ?? null, raw, access)) {
      return {
        access: null,
        refresh: null,
        error: new Error(INACTIVE_ACCOUNT_MESSAGE),
      };
    }
    return { access, refresh: nextRefresh, error: null };
  }
  return {
    access: null,
    refresh: null,
    error: toError("Token refresh failed.", data, raw, status),
  };
}

export async function refreshStaffAccessToken(refresh?: string | null): Promise<{
  access: string | null;
  refresh: string | null;
  error: Error | null;
}> {
  if (!apiBase()) {
    return { access: null, refresh: null, error: new Error("VITE_API_BASE_URL is not set.") };
  }
  const r = refresh ?? getStaffRefreshToken();
  if (!r) {
    return { access: null, refresh: null, error: new Error("No refresh token.") };
  }
  const { ok, status, data, raw } = await authPost(AUTH_PATHS.tokenRefresh, {
    refresh: r,
  });
  const access = ok && data ? extractAccessToken(data) : null;
  const nextRefresh = ok && data ? extractRefreshToken(data) ?? r : null;
  if (ok && access) {
    if (authLoginPayloadIndicatesInactiveUser(data ?? null, raw, access)) {
      return {
        access: null,
        refresh: null,
        error: new Error(INACTIVE_ACCOUNT_MESSAGE),
      };
    }
    return { access, refresh: nextRefresh, error: null };
  }
  return {
    access: null,
    refresh: null,
    error: toError("Staff token refresh failed.", data, raw, status),
  };
}

/** @deprecated Use refreshPortalAccessToken. */
export async function refreshAccessToken(refresh?: string | null) {
  return refreshPortalAccessToken(refresh);
}

export async function logoutContributorRemote(refreshToken?: string | null): Promise<void> {
  if (!apiBase()) {
    return;
  }
  const r = refreshToken ?? getContributorRefreshToken();
  if (!r) {
    return;
  }
  try {
    await authPost(getPortalPaths().logout, { refresh: r }, {
      bearer: getContributorAccessToken(),
    });
  } catch {
    // ignore
  }
}

export async function logoutStaffRemote(refreshToken?: string | null): Promise<void> {
  if (!apiBase()) {
    return;
  }
  const r = refreshToken ?? getStaffRefreshToken();
  if (!r) {
    return;
  }
  try {
    await authPost(AUTH_PATHS.logout, { refresh: r }, {
      bearer: getStaffAccessToken(),
    });
  } catch {
    // ignore
  }
}

/** @deprecated Use logoutContributorRemote. */
export async function logoutRemote(refreshToken?: string | null): Promise<void> {
  return logoutContributorRemote(refreshToken);
}

export async function requestPortalPasswordForgot(
  email: string
): Promise<{ codeReference: string | null; error: Error | null }> {
  if (!apiBase()) {
    return {
      codeReference: null,
      error: new Error("VITE_API_BASE_URL is not set."),
    };
  }
  const { ok, status, data, raw } = await authPost(getPortalPaths().passwordForgot, {
    email: email.trim(),
  });
  if (!ok) {
    return {
      codeReference: null,
      error: toError("Forgot password request failed.", data, raw, status),
    };
  }
  const ref =
    pickString(data, ["code_reference", "codeReference", "reference"]) ?? null;
  return {
    codeReference: ref,
    error: ref ? null : new Error("Missing code reference from server."),
  };
}

export async function requestStaffPasswordForgot(
  email: string
): Promise<{ codeReference: string | null; error: Error | null }> {
  if (!apiBase()) {
    return {
      codeReference: null,
      error: new Error("VITE_API_BASE_URL is not set."),
    };
  }
  const { ok, status, data, raw } = await authPost(AUTH_PATHS.passwordForgot, {
    email: email.trim(),
  });
  if (!ok) {
    return {
      codeReference: null,
      error: toError("Forgot password request failed.", data, raw, status),
    };
  }
  const ref =
    pickString(data, ["code_reference", "codeReference", "reference"]) ?? null;
  return {
    codeReference: ref,
    error: ref ? null : new Error("Missing code reference from server."),
  };
}

/** @deprecated Use requestStaffPasswordForgot or requestPortalPasswordForgot. */
export async function requestPasswordForgot(
  email: string
): Promise<{ codeReference: string | null; error: Error | null }> {
  return requestStaffPasswordForgot(email);
}

export type PasswordResetPayload = {
  email: string;
  otp: string;
  code_reference: string;
  password: string;
  password_confirm: string;
};

export async function resetPortalPassword(
  payload: PasswordResetPayload
): Promise<{ error: Error | null }> {
  if (!apiBase()) {
    return { error: new Error("VITE_API_BASE_URL is not set.") };
  }
  const code = payload.otp.trim().replace(/\s/g, "");
  const { ok, status, data, raw } = await authPost(getPortalPaths().passwordReset, {
    email: payload.email.trim(),
    otp: code,
    code_reference: payload.code_reference.trim(),
    password: payload.password,
    password_confirm: payload.password_confirm,
  });
  if (ok) {
    return { error: null };
  }
  return { error: toError("Password reset failed.", data, raw, status) };
}

export async function resetStaffPassword(
  payload: PasswordResetPayload
): Promise<{ error: Error | null }> {
  if (!apiBase()) {
    return { error: new Error("VITE_API_BASE_URL is not set.") };
  }
  const code = payload.otp.trim().replace(/\s/g, "");
  const { ok, status, data, raw } = await authPost(AUTH_PATHS.passwordReset, {
    email: payload.email.trim(),
    otp: code,
    code_reference: payload.code_reference.trim(),
    password: payload.password,
    password_confirm: payload.password_confirm,
  });
  if (ok) {
    return { error: null };
  }
  return { error: toError("Password reset failed.", data, raw, status) };
}

/** @deprecated Use resetStaffPassword or resetPortalPassword. */
export async function resetPasswordApi(
  payload: PasswordResetPayload
): Promise<{ error: Error | null }> {
  return resetStaffPassword(payload);
}

export type PasswordChangePayload = {
  old_password: string;
  new_password: string;
  new_password_confirm: string;
};

export async function changePortalPasswordApi(
  payload: PasswordChangePayload,
  accessToken?: string | null
): Promise<{ error: Error | null }> {
  if (!apiBase()) {
    return { error: new Error("VITE_API_BASE_URL is not set.") };
  }
  const bearer = accessToken ?? getContributorAccessToken();
  if (!bearer) {
    return { error: new Error("Not signed in.") };
  }
  const { ok, status, data, raw } = await authPost(
    getPortalPaths().passwordChange,
    {
      old_password: payload.old_password,
      new_password: payload.new_password,
      new_password_confirm: payload.new_password_confirm,
    },
    { bearer }
  );
  if (ok) {
    return { error: null };
  }
  return { error: toError("Password change failed.", data, raw, status) };
}

export async function changeStaffPasswordApi(
  payload: PasswordChangePayload,
  accessToken?: string | null
): Promise<{ error: Error | null }> {
  if (!apiBase()) {
    return { error: new Error("VITE_API_BASE_URL is not set.") };
  }
  const bearer = accessToken ?? getStaffAccessToken();
  if (!bearer) {
    return { error: new Error("Not signed in.") };
  }
  const { ok, status, data, raw } = await authPost(
    AUTH_PATHS.passwordChange,
    {
      old_password: payload.old_password,
      new_password: payload.new_password,
      new_password_confirm: payload.new_password_confirm,
    },
    { bearer }
  );
  if (ok) {
    return { error: null };
  }
  return { error: toError("Password change failed.", data, raw, status) };
}

/** @deprecated Use changePortalPasswordApi for contributors. */
export async function changePasswordApi(
  payload: PasswordChangePayload,
  accessToken?: string | null
): Promise<{ error: Error | null }> {
  return changePortalPasswordApi(payload, accessToken);
}
